import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useProfileStore } from '@/store'
import type {
	Notice,
	NoticeInput,
	NoticeListOutput,
	NoticeWithReadStatus,
} from '@/types/notice'
import {
	createNotice,
	deleteNotice,
	getNoticeById,
	getNoticeByIdAdmin,
	getNotices,
	getNoticesAdmin,
	markNoticeAsRead,
	type UploadProgressHandler,
	updateNotice,
} from '../services/notices'

const QUERY_TIME = 2 * 60 * 1000 // 2 minutes

/** Parâmetros da caixa de entrada; compartilhados entre sino, banner e modal para reaproveitar o cache */
export const ACTIVE_NOTICES_PARAMS = {
	isActive: true,
	page: 1,
	limit: 20,
} as const

export function useNotices(params?: {
	isActive?: boolean
	page?: number
	limit?: number
}) {
	const { defaultBranchId } = useProfileStore()
	const queryParams = { ...params, branchId: defaultBranchId ?? undefined }
	return useQuery<NoticeListOutput>({
		queryKey: ['notices', queryParams],
		queryFn: () => getNotices(queryParams),
		staleTime: QUERY_TIME,
		refetchInterval: QUERY_TIME,
	})
}

export function useNoticesAdmin(params?: {
	isActive?: boolean
	page?: number
	limit?: number
}) {
	const { defaultBranchId } = useProfileStore()
	const queryParams = { ...params, branchId: defaultBranchId ?? undefined }
	return useQuery<NoticeListOutput>({
		queryKey: ['noticesAdmin', queryParams],
		queryFn: () => getNoticesAdmin(queryParams),
		staleTime: QUERY_TIME,
		refetchInterval: QUERY_TIME,
	})
}

export function useNoticeById(id: string) {
	// Fora do prefixo ['notices'] e sem refetch automático: um refetch gera nova URL SAS da mídia
	// e reiniciaria o vídeo em reprodução.
	return useQuery<NoticeWithReadStatus>({
		queryKey: ['noticeDetail', id],
		queryFn: () => getNoticeById(id),
		enabled: !!id,
		staleTime: Number.POSITIVE_INFINITY,
		refetchOnWindowFocus: false,
		retry: false,
	})
}

export function useNoticeAdminById(id: string | null) {
	return useQuery<Notice>({
		queryKey: ['noticesAdmin', 'detail', id],
		queryFn: () => getNoticeByIdAdmin(id as string),
		enabled: !!id,
		staleTime: 0,
		retry: false,
	})
}

export function useMarkNoticeAsRead() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: markNoticeAsRead,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['notices'] })
		},
	})
}

export function useCreateNotice() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			data,
			onProgress,
		}: {
			data: NoticeInput
			onProgress?: UploadProgressHandler
		}) => createNotice(data, onProgress),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['notices'] })
			queryClient.invalidateQueries({ queryKey: ['noticesAdmin'] })
		},
	})
}

export function useUpdateNotice() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({
			id,
			data,
			onProgress,
		}: {
			id: string
			data: Partial<NoticeInput>
			onProgress?: UploadProgressHandler
		}) => updateNotice(id, data, onProgress),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['notices'] })
			queryClient.invalidateQueries({ queryKey: ['noticesAdmin'] })
		},
	})
}

export function useDeleteNotice() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (id: string) => deleteNotice(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['notices'] })
			queryClient.invalidateQueries({ queryKey: ['noticesAdmin'] })
		},
	})
}
